import React, { useState } from 'react';
import {
  Bell, Receipt, X, CheckCircle2, CreditCard, Banknote, Smartphone,
  Clock, AlertCircle, Loader2, MessageSquare
} from 'lucide-react';
import { api } from '../../utils/api';
import { toast } from 'react-toastify';

export interface ServiceRequestData {
  id: number;
  table_id: number;
  type: 'CALL_WAITER' | 'REQUEST_BILL';
  payment_method?: 'CASH' | 'CARD' | 'TRANSFER' | null;
  notes?: string | null;
  status: 'PENDING' | 'ATTENDED' | 'CANCELLED';
  created_at: string;
}

interface CustomerServiceModalProps {
  open: boolean;
  onClose: () => void;
  tableId: string | number;
  tableNumber?: string | number;
  activeRequest: ServiceRequestData | null;
  onRefreshActive: () => void;
}

const REASONS_CHIPS = [
  'Asistencia en mesa',
  'Cubiertos / Servilletas',
  'Consulta sobre la carta',
  'Limpiar mesa',
  'Pedir agua o hielo',
];

export const CustomerServiceModal: React.FC<CustomerServiceModalProps> = ({
  open,
  onClose,
  tableId,
  tableNumber,
  activeRequest,
  onRefreshActive,
}) => {
  const [activeTab, setActiveTab] = useState<'CALL_WAITER' | 'REQUEST_BILL'>('CALL_WAITER');
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [customNote, setCustomNote] = useState<string>('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<'CASH' | 'CARD' | 'TRANSFER'>('CASH');
  const [loading, setLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  if (!open) return null;

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const finalNote = activeTab === 'CALL_WAITER'
        ? [selectedReason, customNote.trim()].filter(Boolean).join(' - ')
        : customNote.trim();

      await api.post('/public/service-requests', {
        table_id: tableId,
        type: activeTab,
        payment_method: activeTab === 'REQUEST_BILL' ? selectedPaymentMethod : null,
        notes: finalNote || null,
      });

      toast.success(
        activeTab === 'CALL_WAITER'
          ? '¡Mesero notificado! Ya vamos a tu mesa.'
          : '¡Cuenta solicitada! El mesero se acerca con tu cuenta.',
        { autoClose: 3000 }
      );
      onRefreshActive();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'No se pudo enviar la solicitud. Intenta de nuevo.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRequest = async () => {
    if (!activeRequest) return;
    setCancelling(true);
    try {
      await api.post(`/public/service-requests/${activeRequest.id}/cancel`);
      toast.info('Solicitud cancelada');
      onRefreshActive();
    } catch (err: any) {
      toast.error('No se pudo cancelar la solicitud.');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-xs p-3 sm:items-center sm:p-4 animate-in fade-in duration-200">
      <div className="w-full max-w-lg rounded-3xl bg-[#fdfbf7] p-5 sm:p-6 shadow-2xl border border-[#ebdcc5] text-[#3c2f2f] max-h-[92dvh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between pb-3 border-b border-[#ebdcc5]/60">
          <div className="flex items-center gap-2.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-r from-[#ff6b8b] to-[#f39c12] text-white shadow-md shadow-[#ff6b8b]/20">
              <Bell size={20} />
            </div>
            <div>
              <h2 className="text-lg font-black tracking-tight leading-tight">Servicio en Mesa</h2>
              <p className="text-xs font-semibold text-[#8b7979]">
                {tableNumber ? `Mesa #${tableNumber}` : 'Atención al comensal'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-white border border-[#ebdcc5]/60 text-[#8b7979] hover:text-[#3c2f2f] hover:bg-[#f3ece0]/40 transition"
          >
            <X size={18} />
          </button>
        </div>

        {/* Si ya hay una solicitud activa */}
        {activeRequest && activeRequest.status === 'PENDING' ? (
          <div className="my-5 rounded-2xl bg-amber-50 border border-amber-200/80 p-4.5 space-y-3">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-500 text-white animate-pulse">
                <Clock size={20} />
              </div>
              <div className="flex-1">
                <p className="text-sm font-bold text-amber-900 leading-tight">
                  {activeRequest.type === 'CALL_WAITER' ? '¡Llamada en curso!' : '¡Cuenta solicitada!'}
                </p>
                <p className="text-xs text-amber-700 mt-1 leading-relaxed">
                  {activeRequest.type === 'CALL_WAITER'
                    ? 'Un mesero ha sido notificado y se dirige a tu mesa.'
                    : `El mesero prepara tu cuenta con medio de pago: ${
                        activeRequest.payment_method === 'CASH'
                          ? 'Efectivo 💵'
                          : activeRequest.payment_method === 'CARD'
                          ? 'Datáfono / Tarjeta 💳'
                          : 'Transferencia 📱'
                      }`}
                </p>
                {activeRequest.notes && (
                  <p className="text-[11px] text-amber-800/80 mt-1 italic">
                    Nota: "{activeRequest.notes}"
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-2 pt-1">
              <button
                type="button"
                disabled={cancelling}
                onClick={handleCancelRequest}
                className="flex-1 rounded-xl border border-amber-300 bg-white py-2.5 text-xs font-bold text-amber-800 hover:bg-amber-100/50 transition active:scale-95 disabled:opacity-50"
              >
                {cancelling ? 'Cancelando...' : 'Cancelar solicitud'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 rounded-xl bg-amber-600 py-2.5 text-xs font-bold text-white shadow-sm hover:bg-amber-700 transition active:scale-95"
              >
                Entendido
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Tabs selector */}
            <div className="mt-4 grid grid-cols-2 gap-2 rounded-2xl bg-[#f3ece0]/60 p-1.5 border border-[#ebdcc5]/60">
              <button
                type="button"
                onClick={() => setActiveTab('CALL_WAITER')}
                className={`flex items-center justify-center gap-2 rounded-xl py-2.5 text-xs font-bold transition-all ${
                  activeTab === 'CALL_WAITER'
                    ? 'bg-white text-[#ff6b8b] shadow-sm scale-[1.01]'
                    : 'text-[#6e5d5d] hover:text-[#3c2f2f]'
                }`}
              >
                <Bell size={16} /> Llamar Mesero
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('REQUEST_BILL')}
                className={`flex items-center justify-center gap-2 rounded-xl py-2.5 text-xs font-bold transition-all ${
                  activeTab === 'REQUEST_BILL'
                    ? 'bg-white text-[#f39c12] shadow-sm scale-[1.01]'
                    : 'text-[#6e5d5d] hover:text-[#3c2f2f]'
                }`}
              >
                <Receipt size={16} /> Pedir Cuenta
              </button>
            </div>

            {/* TAB CONTENT: CALL WAITER */}
            {activeTab === 'CALL_WAITER' && (
              <div className="mt-4 space-y-4">
                <div>
                  <label className="block text-xs font-bold text-[#6e5d5d] mb-1.5">
                    ¿En qué podemos ayudarte? (Opcional)
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {REASONS_CHIPS.map(reason => {
                      const isSel = selectedReason === reason;
                      return (
                        <button
                          key={reason}
                          type="button"
                          onClick={() => setSelectedReason(isSel ? '' : reason)}
                          className={`rounded-full px-3 py-1 text-xs font-semibold transition active:scale-95 ${
                            isSel
                              ? 'bg-[#ff6b8b] text-white shadow-xs'
                              : 'bg-white border border-[#ebdcc5] text-[#6e5d5d] hover:bg-[#f3ece0]/40'
                          }`}
                        >
                          {isSel ? `✓ ${reason}` : reason}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#6e5d5d] mb-1">
                    Comentario adicional:
                  </label>
                  <input
                    type="text"
                    value={customNote}
                    onChange={e => setCustomNote(e.target.value)}
                    maxLength={140}
                    placeholder="Ej: cubiertos extra, una servilleta..."
                    className="w-full rounded-2xl bg-white border border-[#ebdcc5] px-4 py-2.5 text-sm text-[#3c2f2f] placeholder-[#8b7979]/50 focus:outline-none focus:border-[#ff6b8b]"
                  />
                </div>
              </div>
            )}

            {/* TAB CONTENT: REQUEST BILL */}
            {activeTab === 'REQUEST_BILL' && (
              <div className="mt-4 space-y-4">
                <div>
                  <label className="block text-xs font-bold text-[#6e5d5d] mb-2">
                    ¿Cómo deseas pagar?
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedPaymentMethod('CASH')}
                      className={`flex flex-col items-center justify-center gap-1.5 rounded-2xl p-3 border text-center transition-all ${
                        selectedPaymentMethod === 'CASH'
                          ? 'border-[#f39c12] bg-[#f39c12]/10 text-[#f39c12] font-black shadow-xs ring-2 ring-[#f39c12]/30'
                          : 'border-[#ebdcc5] bg-white text-[#6e5d5d] hover:bg-[#f3ece0]/30 font-semibold'
                      }`}
                    >
                      <Banknote size={22} />
                      <span className="text-xs">Efectivo</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setSelectedPaymentMethod('CARD')}
                      className={`flex flex-col items-center justify-center gap-1.5 rounded-2xl p-3 border text-center transition-all ${
                        selectedPaymentMethod === 'CARD'
                          ? 'border-[#ff6b8b] bg-[#ff6b8b]/10 text-[#ff6b8b] font-black shadow-xs ring-2 ring-[#ff6b8b]/30'
                          : 'border-[#ebdcc5] bg-white text-[#6e5d5d] hover:bg-[#f3ece0]/30 font-semibold'
                      }`}
                    >
                      <CreditCard size={22} />
                      <span className="text-xs">Datáfono</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setSelectedPaymentMethod('TRANSFER')}
                      className={`flex flex-col items-center justify-center gap-1.5 rounded-2xl p-3 border text-center transition-all ${
                        selectedPaymentMethod === 'TRANSFER'
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700 font-black shadow-xs ring-2 ring-indigo-300'
                          : 'border-[#ebdcc5] bg-white text-[#6e5d5d] hover:bg-[#f3ece0]/30 font-semibold'
                      }`}
                    >
                      <Smartphone size={22} />
                      <span className="text-xs">Transferencia</span>
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#6e5d5d] mb-1">
                    Nota para el cajero (opcional):
                  </label>
                  <input
                    type="text"
                    value={customNote}
                    onChange={e => setCustomNote(e.target.value)}
                    maxLength={140}
                    placeholder="Ej: pago con billete de 50.000, factura a nombre de..."
                    className="w-full rounded-2xl bg-white border border-[#ebdcc5] px-4 py-2.5 text-sm text-[#3c2f2f] placeholder-[#8b7979]/50 focus:outline-none focus:border-[#f39c12]"
                  />
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 rounded-2xl border border-[#ebdcc5] py-3 text-xs font-bold text-[#6e5d5d] hover:bg-[#f3ece0]/40 transition"
              >
                Cerrar
              </button>
              <button
                type="button"
                disabled={loading}
                onClick={handleSubmit}
                className={`flex-2 rounded-2xl py-3.5 px-4 text-xs font-black uppercase tracking-wider text-white shadow-xl transition-all active:scale-[0.98] disabled:opacity-50 ${
                  activeTab === 'CALL_WAITER'
                    ? 'bg-gradient-to-r from-[#ff6b8b] to-[#f39c12] shadow-[#ff6b8b]/20 hover:brightness-105'
                    : 'bg-gradient-to-r from-[#f39c12] to-amber-600 shadow-amber-500/20 hover:brightness-105'
                }`}
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 size={16} className="animate-spin" />
                    <span>Enviando...</span>
                  </div>
                ) : activeTab === 'CALL_WAITER' ? (
                  'Llamar al Mesero 🛎️'
                ) : (
                  'Pedir la Cuenta 💳'
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CustomerServiceModal;
